import React from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, CheckCircle2, AlarmClock, Gavel } from "lucide-react";
import { useFetch } from "../lib/api";
import { SectionLabel } from "../components/shared";

const colorMap = { blue: "#3F4CE0", green: "#0E9355", red: "#E5484D", orange: "#E8590C" };

export default function RegulatorDetail() {
  const { slug } = useParams();
  const { data: regulators, loading } = useFetch("/regulators");
  const r = (regulators || []).find((x) => x.slug === slug);
  if (loading) return <main className="min-h-[50vh] flex items-center justify-center"><div className="w-9 h-9 rounded-full border-[3px] border-[#3F4CE0] border-t-transparent animate-spin" /></main>;
  if (!r) return <main className="max-w-3xl mx-auto px-5 py-24 text-center"><p className="text-[17px]">Regulator not found.</p><Link to="/usa" className="text-[#3F4CE0] underline">Back to USA Hub</Link></main>;
  const accent = colorMap[r.color];
  return (
    <main className="max-w-[1100px] mx-auto px-5 lg:px-8 py-12" data-testid="regulator-detail-page">
      <Link to="/usa" className="inline-flex items-center gap-1.5 text-[13.5px] font-medium text-[#1A1914]/60 hover:text-[#3F4CE0] rp-btn"><ArrowLeft size={14} /> USA Regulatory Hub</Link>
      <p className="mt-6 font-mono-label text-[12px] font-semibold" style={{ color: accent }}>{r.abbr} · est. {r.established}</p>
      <h1 className="mt-2 font-display font-bold text-[36px] lg:text-[52px] tracking-tight leading-[1.05]">{r.name}</h1>
      <p className="mt-3 text-[16px] text-[#1A1914]/60 max-w-3xl">{r.summary}</p>

      <div className="mt-8 grid sm:grid-cols-3 gap-3">
        <div className="bg-white border border-[#E7E0D2] rounded-xl p-4">
          <p className="font-mono-label text-[9.5px] text-[#1A1914]/40">Jurisdiction</p>
          <p className="mt-1.5 text-[13.5px] font-medium leading-snug">{r.jurisdiction}</p>
        </div>
        <div className="bg-white border border-[#E7E0D2] rounded-xl p-4 sm:col-span-2">
          <p className="font-mono-label text-[9.5px] text-[#1A1914]/40">Key regulation</p>
          <p className="mt-1.5 text-[13.5px] font-medium leading-snug">{r.keyRegulation}</p>
        </div>
      </div>

      <div className="mt-10 grid lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          <SectionLabel color={accent}>Key requirements — control catalogue</SectionLabel>
          <div className="space-y-3" data-testid="regulator-requirements">
            {r.keyRequirements.map((k, i) => (
              <div key={i} className="rp-card flex gap-3 bg-white border border-[#E7E0D2] rounded-xl px-5 py-4">
                <CheckCircle2 size={17} className="shrink-0 mt-0.5" style={{ color: accent }} />
                <p className="text-[14px] leading-relaxed">{k}</p>
              </div>
            ))}
          </div>
        </div>
        <aside className="space-y-4">
          <div className="bg-white border border-[#E7E0D2] rounded-2xl p-5">
            <p className="font-mono-label text-[10px] text-[#1A1914]/40 mb-3 flex items-center gap-1.5"><AlarmClock size={12} /> Compliance clocks</p>
            <ul className="space-y-2.5">
              {r.deadlines.map((d) => <li key={d} className="text-[13px] leading-snug pl-3 border-l-2" style={{ borderColor: accent }}>{d}</li>)}
            </ul>
          </div>
          <div className="bg-[#E5484D]/5 border border-[#E5484D]/20 rounded-2xl p-5">
            <p className="font-mono-label text-[10px] text-[#DC2626] mb-2 flex items-center gap-1.5"><Gavel size={12} /> Penalty framework</p>
            <p className="text-[13.5px] leading-relaxed text-[#1A1914]/75">{r.penalties}</p>
          </div>
          <div className="bg-[#14130F] text-white rounded-2xl p-5">
            <p className="font-mono-label text-[10px] text-white/40">Automate it</p>
            <p className="mt-2 text-[13.5px] text-white/70 leading-relaxed">Wire this regulator's clocks into an always-on workflow.</p>
            <Link to="/automations" className="mt-3 inline-block text-[13.5px] font-semibold text-[#12B76A] hover:underline">Browse automations →</Link>
          </div>
        </aside>
      </div>
    </main>
  );
}
